import classNames from 'classnames'
import Fuse, { FuseResult, IFuseOptions } from 'fuse.js'
import { graphql, Link, useStaticQuery } from 'gatsby'
import { get, groupBy, mapValues, sortBy } from 'lodash'
import React, { useContext, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import useProducts from '../../hooks/useProducts'
import {
  interpolate,
  LocaleContext,
  MeasurLocale,
  TranslationKey,
} from '../../locale/i18n'
import {
  blogPostUrl,
  methodUrl,
  productUrl,
  staticPagePaths,
  landingUrl,
} from '../../locale/pathing'
import { Product } from '../../logic/catalog'
import { filterLocale } from '../../logic/contentful'
import { normalizeFormula } from '../../logic/formatting'
import { gaEvents } from '../../logic/ga4'
import {
  ContentfulNode,
  IContentfulBlogPost,
  IContentfulLanding,
  IContentfulMethod,
} from '../../logic/shared-types'
import { StaticPage } from '../../logic/staticPage'
import useOnClickOutside from '../../logic/useOnClickOutside'
import SearchResult, { Match } from '../SearchResult'

import './SiteSearch.scss'

const scrollToActiveItem = () => {
  document
    .querySelector('.searchResult--active')
    ?.scrollIntoView({ block: 'center' })
}

const productSearchItem = (
  {
    descriptionRaw,
    id,
    industryOrMaterial,
    name,
    slug,
    standards,
    typeOfTesting,
  }: Product,
  locale: MeasurLocale,
  t: (key: TranslationKey) => string,
): Match => {
  return {
    extraSearchField1: descriptionRaw,
    extraSearchField2: standards
      .map(({ code, group }) => [group, code].join(' '))
      .join('\t'),
    group: 'product',
    icons: [
      ...industryOrMaterial.map(categoryId => ({
        categoryId,
        fullName: t(`industryOrMaterial.${categoryId}`),
      })),
      ...typeOfTesting.map(categoryId => ({
        categoryId,
        fullName: t(`typeOfTesting.${categoryId}`),
      })),
    ].map(({ categoryId, fullName }) => (
      <img
        className="h-5 w-5"
        key={`${id}-${categoryId}`}
        src={`/category-icons/${encodeURIComponent(categoryId)}.svg`}
        alt={fullName}
        title={fullName}
      />
    )),
    id,
    title: name || '',
    url: productUrl(locale, slug || ''),
  }
}

const fuseOptions: IFuseOptions<Match> = {
  getFn: (obj, path) => {
    return normalizeFormula(get(obj, path, '') as string)
  },
  ignoreLocation: true, // default of false did not find even exact matches towards end of long product names
  includeScore: true, // used for manual sorting in each subgroup
  keys: ['title', 'extraSearchField1', 'extraSearchField2'],
  shouldSort: false, // we sort each subgroup manually to show them in order
  threshold: 0.2,
  useExtendedSearch: true,
}

const SiteSearch = ({
  isVisible,
  setIsVisible,
}: {
  isVisible: boolean
  setIsVisible: (v: boolean) => void
}) => {
  const [searchInput, setSearchInput] = useState('')
  const products = useProducts()

  const debouncedSetSearchInput = useDebouncedCallback((input: string) => {
    setSearchInput(input)
  }, 300)

  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const rootRef = React.useRef<HTMLDivElement>(null)

  const { locale, t } = useContext(LocaleContext)

  const { blogPosts, methods, solutions } = useStaticQuery<{
    blogPosts: {
      nodes: (Pick<
        IContentfulBlogPost,
        'slug' | 'title' | 'whitelistedLocales'
      > &
        ContentfulNode)[]
    }
    methods: {
      nodes: (Pick<
        IContentfulMethod,
        'slug' | 'methodNameShort' | 'methodNameLong' | 'id'
      > &
        ContentfulNode)[]
    }
    solutions: {
      nodes: (Pick<IContentfulLanding, 'heroTitle' | 'slug'> & ContentfulNode)[]
    }
  }>(graphql`
    query {
      blogPosts: allContentfulBlogPost {
        nodes {
          node_locale
          contentful_id
          slug
          title
          whitelistedLocales
        }
      }
      methods: allContentfulMethodPage {
        nodes {
          id
          slug
          methodNameShort
          methodNameLong
          node_locale
        }
      }
      solutions: allContentfulLandingPage(
        filter: { pageType: { eq: "solutions" } }
      ) {
        nodes {
          contentful_id
          heroTitle
          slug
          node_locale
        }
      }
    }
  `)

  const blogPostItems: Match[] = filterLocale(blogPosts.nodes, locale)
    .filter(bp => bp.whitelistedLocales?.includes(locale))
    .map(bp => ({
      group: 'blogPost',
      icons: [],
      id: bp.contentful_id,
      title: bp.title,
      url: blogPostUrl(locale, bp.slug),
    }))
  const productItems: Match[] = products.map(productData =>
    productSearchItem(productData, locale, t),
  )
  const methodItems: Match[] = filterLocale(methods.nodes, locale).map(
    ({ id, methodNameLong, methodNameShort, slug }) => ({
      extraSearchField1: methodNameShort,
      group: 'method',
      id,
      title: methodNameLong,
      url: methodUrl(locale, slug),
    }),
  )
  const solutionItems: Match[] = filterLocale(solutions.nodes, locale).map(
    ({ contentful_id, heroTitle, slug }) => ({
      group: 'solution',
      id: contentful_id,
      title: heroTitle,
      url: landingUrl(locale, slug, 'solutions'),
    }),
  )

  const [fuse] = useState(
    () =>
      new Fuse<Match>(
        productItems.concat(methodItems, solutionItems, blogPostItems),
        fuseOptions,
      ),
  )

  const resultSet = useMemo(() => {
    const resultsByGroup: {
      [c in Match['group']]?: FuseResult<Match>[]
    } = mapValues(groupBy(fuse.search(searchInput.trim()), 'item.group'), g =>
      sortBy(g, 'score').slice(0, 15),
    )
    return new Array<FuseResult<Match>>(0).concat([
      ...(resultsByGroup.product ?? []),
      ...(resultsByGroup.method ?? []),
      ...(resultsByGroup.solution ?? []),
      ...(resultsByGroup.blogPost ?? []),
    ])
  }, [searchInput])

  const showResults = () => {
    setIsVisible(true)
  }
  const hideResults = () => {
    setIsVisible(false)
  }
  useOnClickOutside(rootRef, hideResults)

  const handleKeyDown: React.KeyboardEventHandler = e => {
    if (e.metaKey || e.ctrlKey) {
      return
    }
    showResults()

    e.stopPropagation()

    if (e.key === 'Enter') {
      document.querySelector<HTMLDivElement>('.searchResult--active')?.click()
      return
    }
    if (e.key === 'Tab') {
      hideResults()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()

      if (activeIndex === null) {
        setActiveIndex(0)
      } else if (activeIndex + 1 < resultSet.length) {
        setActiveIndex(activeIndex + 1)
      }
    } else if (e.key === 'ArrowUp' && activeIndex !== null) {
      e.preventDefault()

      if (activeIndex === 0) {
        setActiveIndex(null)
      } else {
        setActiveIndex(activeIndex - 1)
      }
    }
    scrollToActiveItem()
  }

  return (
    <div className="siteSearch" onKeyDown={handleKeyDown} ref={rootRef}>
      <input
        aria-label={t('aria.search')}
        className="siteSearch__input"
        type="search"
        onClick={showResults}
        onChange={e => {
          debouncedSetSearchInput(e.target.value.slice(0, 24))
          setActiveIndex(null)
          gaEvents.queueSearchInputPush(e.target.value)
        }}
        placeholder={t('search.placeholder')}
        title={t('search.placeholder')}
      />
      <div
        className={classNames('siteSearch__resultBox', {
          'siteSearch__resultBox--visible': isVisible,
        })}
      >
        <div className="siteSearch__results">
          {searchInput.trim() ? (
            <>
              {resultSet.length ? (
                resultSet.map(({ item: result }, i) => {
                  return (
                    <React.Fragment key={result.id}>
                      {(i === 0 ||
                        result.group !== resultSet[i - 1]?.item.group) && (
                        <div className="siteSearch__resultSubHeading">
                          {t(`search.groupHeading.${result.group}`)}
                        </div>
                      )}
                      <SearchResult
                        key={`${i}-${result.id}${
                          activeIndex === i ? '-active' : ''
                        }`}
                        resultId={result.id}
                        isActive={activeIndex === i}
                        match={result}
                        searchTerm={searchInput}
                      />
                    </React.Fragment>
                  )
                })
              ) : (
                <div className="siteSearch__noResults siteSearch__resultSection">
                  {t('search.noResults')}
                </div>
              )}
            </>
          ) : (
            <div className="siteSearch__help siteSearch__resultSection">
              {t('search.inContextHelp')}
            </div>
          )}
        </div>
        {resultSet.length > 0 && (
          <div className="siteSearch__resultFooter">
            {interpolate(t('search.alsoOtherMethods'), {
              link: text => (
                <Link
                  rel="nofollow"
                  to={staticPagePaths[StaticPage.CONTACT][locale]}
                  key="alsoOtherMethodsLink"
                >
                  {text}
                </Link>
              ),
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default SiteSearch
